import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, CheckCircle2, Circle, Play, Target, Lock } from "lucide-react";
import { ExpertBadge } from "@/components/ExpertBadge";

export default function ModuloDetail() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/modules/${moduleId}`).then((r) => setModule(r.data)).catch(() => setError(true));
  }, [moduleId]);

  if (error) {
    return (
      <div className="pt-10 text-center">
        <p className="font-bold">Modulo non trovato.</p>
        <Link to="/moduli" className="mt-3 inline-block underline text-sm">Torna ai moduli</Link>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="pt-6 space-y-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-32 w-full rounded-3xl" />
        <Skeleton className="h-20 w-full rounded-3xl" />
      </div>
    );
  }

  const isLocked = (lessonId) => !module.hasFullAccess && module.freeLessonId !== lessonId && module.freeLessonId !== undefined;
  const nextLesson = module.lessons.find((l) => !l.completed && !isLocked(l.id));
  const hasExample = module.lessons.some((l) => l.isExample);

  return (
    <div className="pt-6">
      <button onClick={() => navigate("/moduli")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--text-2)] hover:text-[color:var(--ink)] transition-colors duration-200" data-testid="module-back-button">
        <ArrowLeft className="w-4 h-4" /> Moduli
      </button>

      <div className="mt-4 fade-up">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] tracking-widest uppercase border border-[color:var(--hairline)] rounded-full px-2 py-0.5">
            Blocco {module.blocco} · {module.bloccoNome}
          </span>
          <span className="font-mono text-[11px] text-[color:var(--text-2)]">{module.ore}h</span>
          {hasExample && (
            <span className="text-[10px] rounded-full bg-[color:var(--bg-subtle)] text-[color:var(--text-2)] border border-[color:var(--hairline)] px-2 py-0.5 font-semibold uppercase tracking-wide">
              Contenuto di esempio
            </span>
          )}
        </div>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight leading-tight" data-testid="module-title">{module.titolo}</h1>
        <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6">{module.descrizione}</p>
        <ExpertBadge moduleId={module.id} />
      </div>

      {nextLesson && (
        <button
          onClick={() => navigate(`/lezione/${nextLesson.id}`)}
          data-testid="module-start-next-lesson-button"
          className="press mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
        >
          <Play className="w-4 h-4" fill="currentColor" />
          {module.lessons.some((l) => l.completed) ? "Continua: " : "Inizia: "}
          <span className="truncate max-w-[200px]">{nextLesson.titolo}</span>
        </button>
      )}

      <section className="mt-8">
        <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase mb-4">Lezioni</h2>
        {module.lessons.length === 0 && (
          <p className="text-sm text-[color:var(--text-2)] py-6 text-center rounded-3xl border border-dashed border-[color:var(--hairline)]">
            Le lezioni di questo modulo arriveranno presto.
          </p>
        )}
        <div className="space-y-2.5">
          {module.lessons.map((l, i) => {
            const locked = isLocked(l.id);
            return (
            <Link
              key={l.id}
              to={locked ? "/sblocca" : `/lezione/${l.id}`}
              data-testid={`lesson-item-${l.id}`}
              className={`flex items-center gap-3 rounded-2xl border border-[color:var(--hairline)] px-4 py-4 transition-colors duration-200 fade-up ${
                locked ? "bg-[color:var(--bg-subtle)] hover:bg-white" : "bg-white hover:bg-[color:var(--bg-subtle)]"
              }`}
            >
              {locked ? (
                <Lock className="w-5 h-5 shrink-0 text-[color:var(--text-2)]" aria-hidden="true" />
              ) : l.completed ? (
                <CheckCircle2 className="w-5 h-5 shrink-0 text-[color:var(--ink)]" fill="var(--volt)" />
              ) : (
                <Circle className="w-5 h-5 shrink-0 text-[color:var(--hairline)]" />
              )}
              <div className="min-w-0 flex-1">
                <p className={`font-bold text-sm leading-5 ${locked ? "text-[color:var(--text-2)]" : ""}`}>{l.titolo}</p>
                <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-0.5">
                  {locked
                    ? "Riservata all'accesso completo"
                    : l.type === "scheda"
                    ? `Scheda di studio${l.hours ? ` · ${l.hours} ore` : ""}`
                    : l.type === "cards"
                    ? `Lezione ${i + 1} · ${l.cardCount || 0} schede${l.videoStatus === "coming_soon" ? " · video in arrivo" : ""}`
                    : `Lezione ${i + 1} · ${l.quizQuestions > 0 ? `${l.quizQuestions} domande di quiz` : "senza quiz"}`}
                </p>
              </div>
              {l.draft && (
                <span
                  className="shrink-0 text-[10px] font-extrabold uppercase tracking-wide rounded-full text-white px-2 py-1"
                  style={{ background: "#B45309" }}
                  data-testid={`lesson-draft-badge-${l.id}`}
                >
                  Bozza
                </span>
              )}
              {locked ? (
                <span className="shrink-0 text-[10px] font-extrabold uppercase tracking-wide rounded-full border border-[color:var(--hairline)] text-[color:var(--text-2)] px-2 py-1" data-testid={`lesson-locked-badge-${l.id}`}>
                  Sblocca
                </span>
              ) : l.completed ? (
                <span className="shrink-0 text-[10px] font-extrabold uppercase tracking-wide rounded-full bg-[color:var(--volt)] px-2 py-1">
                  Fatta
                </span>
              ) : null}
            </Link>
            );
          })}
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-extrabold tracking-tight text-sm">Quiz del modulo</h2>
            <p className="text-xs text-[color:var(--text-2)] mt-1 leading-5">Domande solo da “{module.titolo}”. Allenamento mirato.</p>
          </div>
          <button
            onClick={() => navigate(module.hasFullAccess === false ? "/sblocca" : `/esame?mode=module&moduleId=${module.id}`)}
            data-testid="module-start-quiz-button"
            className="press shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-sm font-extrabold px-4 py-2.5 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
          >
            {module.hasFullAccess === false ? <Lock className="w-4 h-4" aria-hidden="true" /> : <Target className="w-4 h-4" />}
            {module.hasFullAccess === false ? "Sblocca" : "Inizia"}
          </button>
        </div>
      </section>
    </div>
  );
}
